import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useNotifications } from '@/hooks/useNotifications'

interface Lead {
  id: string
  reference: string
  name: string
  email: string
  company: string
  budget_range: string
  timeline: string
  status: string
  quality_score: number
  created_at: string
}

export const Leads: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const limit = 20
  const { markLeadsAsViewed } = useNotifications()

  useEffect(() => {
    markLeadsAsViewed()
  }, [markLeadsAsViewed])

  const { data, isLoading } = useQuery({
    queryKey: ['leads', page, search, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('page', String(page))
      params.append('limit', String(limit))
      if (search) params.append('search', search)
      if (statusFilter) params.append('status', statusFilter)
      const response = await api.get(`/leads?${params.toString()}`)
      return response.data
    },
  })

  const leads: Lead[] = data?.data || []
  const meta = data?.meta || { total: 0, totalPages: 1 }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-green-500/20 text-green-400'
      case 'contacted': return 'bg-blue-500/20 text-blue-400'
      case 'qualified': return 'bg-purple-500/20 text-purple-400'
      case 'converted': return 'bg-teal-500/20 text-teal-400'
      case 'lost': return 'bg-red-500/20 text-red-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  const getStatusLabel = (status: string) => {
    const translated = t(`leads.${status}`)
    return translated !== `leads.${status}` ? translated : status.replace('_', ' ')
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">{t('leads.title')}</h1>

      {/* Filters */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            type="text"
            placeholder={t('leads.searchPlaceholder')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="flex-1 px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
          />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
          >
            <option value="">{t('leads.allStatus')}</option>
            <option value="new">{t('leads.new')}</option>
            <option value="contacted">{t('leads.contacted')}</option>
            <option value="qualified">{t('leads.qualified')}</option>
            <option value="converted">{t('leads.converted')}</option>
            <option value="lost">{t('leads.lost')}</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">{t('leads.loading')}</div>
        ) : leads.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <p className="text-lg mb-2">{t('leads.noLeadsTitle')}</p>
            <p className="text-sm">{t('leads.noLeadsDesc')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('leads.reference')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('leads.name')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('leads.email')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('common.status')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('leads.budget')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('leads.timeline')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('leads.created')}</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                      onClick={() => navigate(`/leads/${lead.id}`)}
                    >
                      <td className="px-4 py-3 text-[#006cd4] font-mono text-sm">{lead.reference}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{lead.name}</div>
                        {lead.company && <div className="text-sm text-gray-500">{lead.company}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-300">{lead.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                          {getStatusLabel(lead.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">{lead.budget_range || '-'}</td>
                      <td className="px-4 py-3 text-gray-300">{lead.timeline || '-'}</td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
              <div className="text-sm text-gray-400">
                {t('common.showingOf', { count: leads.length, total: meta.total })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.previous')}
                </button>
                <span className="px-3 py-1 text-sm">
                  {t('common.pageOf', { page, total: meta.totalPages || 1 })}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(meta.totalPages || 1, p + 1))}
                  disabled={page >= meta.totalPages}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
